import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

// material
import { Dialog } from '@material-ui/core';
import MailForm from 'components/modules/crm/mail/MailForm';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import UserContext from 'components/context/UserContext';

// HTTP
import ModalUtils from 'utils/ModalUtils';
import { useMutation } from '@apollo/client';
import MessageUtils from 'utils/MessageUtils';
import MailMutation from 'services/graphQL/mutate/crm/MailMutation';
import {
    companyDomainEmailConfirmMessage,
    hasCompanyDomainEmail,
} from 'utils/customer/CompanyDomainEmail';

const EmailForm = ({
    open, onClose, crmId, email, leadCode,
    prospectLanguage,
}) => {
    const { userInformation = {} } = useContext(UserContext);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [pendingRecord, setPendingRecord] = useState(null);
    const [sendEmail, { loading }] = useMutation(MailMutation.SEND_EMAIL_BY_CRM_ID);

    const executeSend = async (record) => {
        try {
            const input = {
                cc: record.cc,
                bcc: record.bcc,
                subject: record.subject,
                body: record.bodyMessage,
            };

            const response = await sendEmail({ variables: { input, crmId } });

            if (response?.data?.sendEmailByCRMId) {
                onClose();
            } else {
                ModalUtils.errorMessage(null, MessageUtils.getGenericError('Send', 'Email'));
            }
        } catch (ex) {
            ModalUtils.errorMessage(null, ex.message || MessageUtils.getGenericError('Send', 'Email'));
        }
    };

    const closeConfirm = () => {
        setOpenConfirm(false);
        setPendingRecord(null);
    };

    const sendMessage = async (record) => {
        if (hasCompanyDomainEmail(record.to, userInformation.companyListDomain)) {
            setPendingRecord(record);
            setOpenConfirm(true);
            return;
        }

        await executeSend(record);
    };

    const onConfirmSend = async () => {
        const record = pendingRecord;
        closeConfirm();

        if (record) {
            await executeSend(record);
        }
    };

    if (!open) {
        return null;
    }

    return (
        <>
            <Dialog
                fullWidth
                open={open}
                maxWidth="md"
                onClose={onClose}
                aria-labelledby="alert-dialog-title"
                scroll="paper"
                disableEnforceFocus
            >
                <MailForm
                    disabledTo
                    email={email}
                    prospectLanguage={prospectLanguage}
                    showEmailTemplate
                    loading={loading}
                    onClose={onClose}
                    leadCode={leadCode}
                    onSendMessage={sendMessage}
                />
            </Dialog>
            <ConfirmDialog
                open={openConfirm}
                title="Confirm Send Email"
                description={companyDomainEmailConfirmMessage}
                titlePrimary="Yes"
                titleSecondary="No"
                variant="outlined"
                onClose={closeConfirm}
                onClickSecondary={closeConfirm}
                onClickPrimary={onConfirmSend}
                disablePrimaryButton={loading}
                disableSecondaryButton={loading}
            />
        </>
    );
};

EmailForm.propTypes = {
    email: PropTypes.string,
    leadCode: PropTypes.number,
    open: PropTypes.bool.isRequired,
    crmId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    prospectLanguage: PropTypes.string,
};

EmailForm.defaultProps = {
    email: null,
    leadCode: null,
    prospectLanguage: 'English',
};

export default EmailForm;
